import { supabase } from './supabase'
import { fileDAttente, marquerEchec, retirerDeLaFile } from './db'
import { annotationsEnAttente, envoyerAnnotations } from './annotations'
import { envoyerSuivis, suivisEnAttente } from './suivis'

let enCours = false
const abonnes = new Set<(restants: number) => void>()

export function surChangementFile(f: (restants: number) => void): () => void {
  abonnes.add(f)
  void compter().then(f)
  return () => abonnes.delete(f)
}

async function compter(): Promise<number> {
  return (await fileDAttente()).length + (await annotationsEnAttente()) + (await suivisEnAttente())
}

async function prevenir(): Promise<void> {
  const n = await compter()
  abonnes.forEach((f) => f(n))
}

/**
 * Vide la file d'attente, dans l'ordre de création.
 *
 * Le `client_uuid` est généré sur l'appareil et porte une contrainte d'unicité
 * en base : un envoi retenté après une coupure ne crée jamais de doublon —
 * la seconde insertion échoue proprement et l'élément sort de la file.
 */
export async function synchroniser(): Promise<void> {
  if (enCours || !navigator.onLine) return
  enCours = true
  try {
    const { data: session } = await supabase.auth.getSession()
    const utilisateur = session.session?.user
    if (!utilisateur) return

    for (const p of await fileDAttente()) {
      try {
        const nom = `${p.chantier_id}/${p.client_uuid}.jpg`
        const { error: erreurFichier } = await supabase.storage
          .from('photos')
          .upload(nom, p.blob, { contentType: 'image/jpeg', upsert: true })
        if (erreurFichier) throw erreurFichier

        const { error: erreurLigne } = await supabase.from('photos').insert({
          chantier_id: p.chantier_id,
          prise_par: utilisateur.id,
          prise_le: p.prise_le,
          source: p.source,
          origine_date: p.origine_date,
          note: p.note,
          fichier_path: nom,
          largeur: p.largeur,
          hauteur: p.hauteur,
          client_uuid: p.client_uuid
        })

        // 23505 = violation d'unicité : la photo est déjà passée. Rien à refaire.
        if (erreurLigne && erreurLigne.code !== '23505') throw erreurLigne

        await retirerDeLaFile(p.client_uuid)
        await prevenir()
      } catch (e) {
        await marquerEchec(p.client_uuid, e instanceof Error ? e.message : String(e))
        // On s'arrête au premier échec : sur une connexion faible, insister
        // sur les suivants ne ferait qu'épuiser la pile et la batterie.
        break
      }
    }

    // Annotations des plans : du texte seulement, l'envoi est rapide.
    await envoyerAnnotations(utilisateur.id, prevenir)

    // Suivis (tâches, déficiences, QRT), commentaires et photos liées.
    await envoyerSuivis(utilisateur.id, prevenir)
  } finally {
    enCours = false
    await prevenir()
  }
}

/** Relance la synchronisation au retour du réseau et périodiquement. */
export function demarrerSynchronisation(): () => void {
  const relancer = () => void synchroniser()
  window.addEventListener('online', relancer)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') relancer()
  })
  const minuterie = window.setInterval(relancer, 60_000)
  relancer()
  return () => {
    window.removeEventListener('online', relancer)
    window.clearInterval(minuterie)
  }
}
